import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { Button } from 'ui/baseComponents'

const REASONS = [
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'NON_RESPONSIVE', label: 'Non-Responsive' },
]

const CATEGORIES = [
  { value: 'MOBILITY', label: 'Mobility' },
  { value: 'TREMOR_DYSKINESIA', label: 'Tremor & Dyskinesia' },
  { value: 'SLEEP', label: 'Sleep' },
]

const emptyForm = {
  patientId: '',
  reason: '',
  categories: [],
  detail: '',
}

const ExemptionDialog = ({ open, onClose, onSubmit, exemption, saving }) => {
  const isEdit = !!exemption
  const [form, setForm] = useState(emptyForm)

  useEffect(() => {
    if (open) {
      setForm(
        exemption
          ? {
              patientId: exemption.patientId,
              reason: exemption.reason,
              categories: exemption.categories || [],
              detail: exemption.detail || '',
            }
          : emptyForm,
      )
    }
  }, [open, exemption])

  const handleCategoryToggle = useCallback((value) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.includes(value)
        ? prev.categories.filter((c) => c !== value)
        : [...prev.categories, value],
    }))
  }, [])

  const canSubmit =
    form.patientId.trim() &&
    form.reason &&
    form.categories.length > 0 &&
    !saving

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return
    onSubmit(form)
  }, [canSubmit, form, onSubmit])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Compliance Exemption' : 'Create Compliance Exemption'}
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: '8px !important',
        }}
      >
        <TextField
          label="Patient ID"
          value={form.patientId}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, patientId: e.target.value }))
          }
          disabled={isEdit}
          required
          fullWidth
          size="small"
        />

        <FormControl size="small" required fullWidth>
          <InputLabel>Reason</InputLabel>
          <Select
            value={form.reason}
            label="Reason"
            onChange={(e) =>
              setForm((prev) => ({ ...prev, reason: e.target.value }))
            }
          >
            {REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl component="div" required>
          <FormLabel>Categories</FormLabel>
          <FormGroup row>
            {CATEGORIES.map((cat) => (
              <FormControlLabel
                key={cat.value}
                sx={{ ml: 0, mr: 3, gap: 0.5 }}
                control={
                  <Checkbox
                    checked={form.categories.includes(cat.value)}
                    onChange={() => handleCategoryToggle(cat.value)}
                    size="small"
                  />
                }
                label={cat.label}
              />
            ))}
          </FormGroup>
        </FormControl>

        <TextField
          label="Detail (optional)"
          value={form.detail}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, detail: e.target.value }))
          }
          multiline
          rows={3}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button type="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ExemptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  exemption: PropTypes.shape({
    patientId: PropTypes.string,
    reason: PropTypes.string,
    categories: PropTypes.arrayOf(PropTypes.string),
    detail: PropTypes.string,
  }),
  saving: PropTypes.bool,
}

export default ExemptionDialog
